import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { UserService } from '@core/services/user.service';
import {
  UserPasswordModel,
  UpdateUserModel,
  RoleResponseModel,
  RoleFeatureResponseModel,
  CreationUserPayloadModel,
  UserProfile,
  getUserRole,
  UserPartnerRoleModel,
  AssignUserRoleModel,
} from '@core/models';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, Observable, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { AuthorizationService } from '@core/services/authorization.service';
import { DomSanitizer } from '@angular/platform-browser';
import { LocalhostKeys } from '@core/constants';

type UserState = {
  isLoading: boolean;
  isLoadingAvatar: boolean;
  isLoadingFeatures: boolean;
  userAvatar: any;
  user: UserProfile;
  enable2FA: boolean;
  notifications: any;
  roleData: RoleResponseModel;
  roleFeatures: RoleFeatureResponseModel[];
  userRole: UserPartnerRoleModel;
};

const initialState: UserState = {
  isLoading: false,
  isLoadingAvatar: false,
  isLoadingFeatures: false,
  userRole: {
    roleId: -1,
    roleName: '',
  },

  userAvatar: '',
  user: {
    object_id: '',
    fullName: '',
    email: '',
    firstName: '',
    lastName: '',
    street: '',
    postalCode: '',
    city: '',
    country: '',
    phone: '',
    positionType: '',
    status: '',
    terms_of_service: '0',
    language: '',
    partners: [],
  },
  enable2FA: false,
  notifications: [],
  roleData: {
    numberOfItems: 0,
    roles: [],
  },
  roleFeatures: [],
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      userService = inject(UserService),
      authorizationService = inject(AuthorizationService),
      translateService = inject(TranslateService),
      sanitizer = inject(DomSanitizer),
    ) => ({
      updateUserMethod: rxMethod<{ user: UpdateUserModel; oid: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(payload => {
            return userService.updateUser(payload.user, payload.oid).pipe(
              tapResponse({
                next: (res: any) => {
                  if (payload.user.language) {
                    const langCode = payload.user.language.toLowerCase();
                    translateService.use(langCode);
                    localStorage.setItem(LocalhostKeys.LANGUAGE, langCode);
                  }

                  const userUpdated: UserProfile = {
                    ...store.user(),
                    ...payload.user,
                  };
                  patchState(store, { user: userUpdated, isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),
      deleteUserFromPartnerMethod(partnerOid: string, userOid: string): Observable<Object> {
        patchState(store, { isLoading: true });
        return userService.deleteUserFromPartner(partnerOid, userOid).pipe(
          tap({
            next: () => {
              patchState(store, { isLoading: false });
            },
            error: () => {
              patchState(store, { isLoading: false });
            },
          }),
        );
      },
      assignUserRoleMethod(payload: AssignUserRoleModel): Observable<unknown> {
        patchState(store, { isLoading: true });
        return userService.assignUserRole(payload).pipe(
          tap({
            next: () => {
              patchState(store, { isLoading: false });
            },
            error: () => {
              patchState(store, { isLoading: false });
            },
          }),
        );
      },

      changeUserPasswordMethod: rxMethod<UserPasswordModel>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(payload => {
            return userService.changeUserPassword(payload).pipe(
              tapResponse({
                next: (res: any) => {
                  patchState(store, { isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),

      add2FAMethod: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(verifyKey => {
            return userService.add2FA(verifyKey).pipe(
              tapResponse({
                next: (res: any) => {
                  patchState(store, { enable2FA: res.value, isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),

      changeUserNotificationsMethod: rxMethod<any>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(payload => {
            return userService.changeUserNotifications(payload).pipe(
              tapResponse({
                next: (res: any) => {
                  patchState(store, { isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),

      getRoleFeaturesMethod: rxMethod<number>(
        pipe(
          tap(() => patchState(store, { isLoadingFeatures: true })),
          switchMap(roleId => {
            return authorizationService.getRoleFeatures(roleId).pipe(
              tapResponse({
                next: res => {
                  patchState(store, {
                    roleFeatures: res,
                    isLoadingFeatures: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoadingFeatures: false });
                },
              }),
            );
          }),
        ),
      ),

      createUserMethod(userCreate: CreationUserPayloadModel): Observable<unknown> {
        patchState(store, { isLoading: true });
        return userService.createUser(userCreate).pipe(
          tap({
            next: () => {
              patchState(store, { isLoading: false });
            },
            error: () => {
              patchState(store, { isLoading: false });
            },
          }),
        );
      },

      getRolesMethod: rxMethod<void>(
        pipe(
          switchMap(() => {
            // Roles are global reference data that don't change within a
            // session, so skip the request once they're loaded.
            if (store.roleData().roles.length > 0) {
              return EMPTY;
            }
            patchState(store, { isLoading: true });
            return authorizationService.getRoles().pipe(
              tapResponse({
                next: (res: RoleResponseModel) => {
                  patchState(store, { roleData: res, isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),

      getUserMethod: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(oid => {
            return userService.getUser(oid).pipe(
              tapResponse({
                next: (res: UserProfile) => {
                  // Partner-scoped data (company detail, users, roles) is loaded
                  // on demand by the My Company page rather than eagerly here, so
                  // an app bootstrap no longer fetches data most pages never use.
                  patchState(store, {
                    user: res,
                    isLoading: false,
                    userRole: getUserRole(res),
                  });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),

      getUserAvatarMethod: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoadingAvatar: true })),
          switchMap(oid => {
            return userService.getUserAvatar(oid).pipe(
              tapResponse({
                next: res => {
                  if (res.type === 'application/json') {
                    patchState(store, {
                      isLoadingAvatar: false,
                    });
                    return;
                  }
                  let objectURL = URL.createObjectURL(res);
                  const userAvatar = sanitizer.bypassSecurityTrustUrl(objectURL);
                  patchState(store, {
                    userAvatar: userAvatar,
                    isLoadingAvatar: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoadingAvatar: false });
                },
              }),
            );
          }),
        ),
      ),

      updateUserAvatarMethod: rxMethod<{ avatar: any; oid: string }>(
        pipe(
          tap(() => patchState(store, { isLoadingAvatar: true })),
          switchMap(payload => {
            return userService.updateUserAvatar(payload.avatar, payload.oid).pipe(
              tapResponse({
                next: () => {
                  let objectURL = URL.createObjectURL(payload.avatar);
                  const userAvatar = sanitizer.bypassSecurityTrustUrl(objectURL);
                  patchState(store, { userAvatar, isLoadingAvatar: false });
                },
                error: () => {
                  patchState(store, { isLoadingAvatar: false });
                },
              }),
            );
          }),
        ),
      ),

      switchUserMethod: rxMethod<{ oid: string; partnerOid: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(payload => {
            return userService.switchUser(payload.oid, payload.partnerOid).pipe(
              tapResponse({
                next: () => {
                  let partners = store.user()!.partners;
                  partners = partners.map(p => ({
                    ...p,
                    is_active: p.partnerOid === payload.partnerOid,
                  }));

                  const userUpdated = {
                    ...store.user(),
                    userRole: getUserRole(store.user()),
                    partners,
                  };
                  patchState(store, { user: userUpdated, isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),
      resendInvite(userOid: string): Observable<Object> {
        return userService.resendInvite({ user_oid: userOid });
      },
    }),
  ),
);
